import { audioContext } from "./context.js";

/**
 * Parameter smoothing constant. Every user-facing control writes through
 * setTargetAtTime rather than assigning .value, so moves are ramped rather
 * than stepped (M5) - a stepped gain change is an audible click.
 */
const SMOOTHING = 0.015;

/** EQ travel: full kill at the bottom, modest boost at the top. */
const EQ_KILL_DB = -40;
const EQ_BOOST_DB = 6;

export const EQ_BANDS = ["low", "mid", "high"] as const;
export type EqBand = (typeof EQ_BANDS)[number];

function ramp(param: AudioParam, value: number): void {
  param.setTargetAtTime(value, audioContext().currentTime, SMOOTHING);
}

/**
 * Map a 0..1 control position to decibels, with unity at the centre detent.
 *
 * A straight linear map would put unity at an arbitrary position and make the
 * centre useless as a reference; DJ EQs are built around "12 o'clock is flat".
 */
export function positionToDb(position: number): number {
  const clamped = Math.max(0, Math.min(1, position));
  if (clamped <= 0.5) {
    return EQ_KILL_DB * (1 - clamped / 0.5);
  }
  return EQ_BOOST_DB * ((clamped - 0.5) / 0.5);
}

/**
 * Constant-power gain curves for a crossfade between two fader positions.
 *
 * Returned rather than applied so the endpoints can be checked without an audio
 * context: the first sample of the incoming channel's curve must be silence,
 * which is precisely what went wrong when the start position was implicit.
 */
export function constantPowerCurves(
  from: number,
  to: number,
  points = 128,
): { first: Float32Array; second: Float32Array } {
  const first = new Float32Array(points);
  const second = new Float32Array(points);
  for (let i = 0; i < points; i++) {
    const position = from + ((to - from) * i) / (points - 1);
    const angle = ((position + 1) / 2) * (Math.PI / 2);
    first[i] = Math.cos(angle);
    second[i] = Math.sin(angle);
  }
  return { first, second };
}

/** One mixer channel: 3-band EQ, trim, and the crossfader's gain stage. */
export class ChannelStrip {
  readonly input: GainNode;
  readonly output: GainNode;

  private readonly filters: Record<EqBand, BiquadFilterNode>;
  private readonly trim: GainNode;
  private readonly crossfade: GainNode;
  private readonly eqPositions: Record<EqBand, number> = { low: 0.5, mid: 0.5, high: 0.5 };
  private trimPosition = 0.8;

  constructor() {
    const ctx = audioContext();
    this.input = ctx.createGain();

    const low = ctx.createBiquadFilter();
    low.type = "lowshelf";
    low.frequency.value = 220;

    const mid = ctx.createBiquadFilter();
    mid.type = "peaking";
    mid.frequency.value = 1000;
    mid.Q.value = 0.9;

    const high = ctx.createBiquadFilter();
    high.type = "highshelf";
    high.frequency.value = 3800;

    this.filters = { low, mid, high };
    this.trim = ctx.createGain();
    // Squared to match setTrim; assigning the raw position here would make the
    // opening level disagree with the fader the moment it is first touched.
    this.trim.gain.value = this.trimPosition * this.trimPosition;
    this.crossfade = ctx.createGain();
    this.output = ctx.createGain();

    this.input.connect(low).connect(mid).connect(high).connect(this.trim);
    this.trim.connect(this.crossfade).connect(this.output);
  }

  setEq(band: EqBand, position: number): void {
    this.eqPositions[band] = position;
    ramp(this.filters[band].gain, positionToDb(position));
  }

  getEq(band: EqBand): number {
    return this.eqPositions[band];
  }

  resetEq(): void {
    for (const band of EQ_BANDS) this.setEq(band, 0.5);
  }

  setTrim(position: number): void {
    this.trimPosition = Math.max(0, Math.min(1, position));
    // Squared, so the fader behaves more like a level control than a linear
    // amplitude ramp, which feels top-heavy.
    ramp(this.trim.gain, this.trimPosition * this.trimPosition);
  }

  getTrim(): number {
    return this.trimPosition;
  }

  /** Set by the mixer's crossfader; not a user control on its own. */
  setCrossfadeGain(gain: number): void {
    ramp(this.crossfade.gain, gain);
  }

  /**
   * Commit a gain curve to the audio clock, all at once.
   *
   * Used for automated transitions: once scheduled, the fade completes on time
   * whether or not the tab is running JavaScript (C7).
   */
  scheduleCrossfadeCurve(curve: Float32Array, startAt: number, duration: number): void {
    const param = this.crossfade.gain;
    const now = audioContext().currentTime;
    const when = Math.max(startAt, now);
    const head = curve[0] as number;

    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    // Ease to where the curve begins rather than jumping to it. The fade is
    // armed well before it runs, so this is a slow move to the starting
    // position, not a step at the moment the transition starts.
    param.linearRampToValueAtTime(head, Math.min(now + 1.5, when));
    param.setValueAtTime(head, when);
    param.setValueCurveAtTime(curve, when, Math.max(0.01, duration));
  }

  /** Gain actually in effect, for reporting what the fader is really doing. */
  crossfadeGain(): number {
    return this.crossfade.gain.value;
  }

  cancelScheduled(): void {
    const param = this.crossfade.gain;
    const now = audioContext().currentTime;
    const current = param.value;
    param.cancelScheduledValues(now);
    param.setValueAtTime(current, now);
  }
}

export interface MeterReading {
  peak: number;
  rms: number;
  clipping: boolean;
}

export class Mixer {
  readonly channels: Map<string, ChannelStrip> = new Map();
  readonly master: GainNode;
  /** Post-limiter, so taps here hear exactly what the speakers get. */
  readonly monitorPoint: AudioNode;

  private readonly limiter: DynamicsCompressorNode;
  private readonly analyser: AnalyserNode;
  private readonly meterBuffer: Float32Array<ArrayBuffer>;
  private crossfaderPosition = 0;
  private masterPosition = 0.8;

  constructor(channelIds: string[]) {
    const ctx = audioContext();

    this.master = ctx.createGain();
    this.master.gain.value = this.masterPosition * this.masterPosition;

    // Two channels plus EQ boost can exceed full scale even with per-track
    // auto-gain. Clipping at the destination is hard distortion, so a limiter
    // sits in front of it.
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -3;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.002;
    this.limiter.release.value = 0.15;

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    // Backed by an explicit ArrayBuffer: getFloatTimeDomainData will not accept
    // a view that might be over a SharedArrayBuffer.
    this.meterBuffer = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4));

    for (const id of channelIds) {
      const strip = new ChannelStrip();
      strip.output.connect(this.master);
      this.channels.set(id, strip);
    }

    this.master.connect(this.limiter);
    this.limiter.connect(this.analyser);
    this.analyser.connect(ctx.destination);
    this.monitorPoint = this.limiter;

    this.setCrossfader(0);
  }

  channel(id: string): ChannelStrip {
    const strip = this.channels.get(id);
    if (!strip) throw new Error(`no mixer channel ${id}`);
    return strip;
  }

  /**
   * Crossfader position: -1 is fully the first channel, +1 fully the second.
   *
   * Constant-power rather than linear, so the total level stays roughly even
   * through the middle instead of dipping where both are half open.
   */
  setCrossfader(position: number): void {
    this.crossfaderPosition = Math.max(-1, Math.min(1, position));
    const t = ((this.crossfaderPosition + 1) / 2) * (Math.PI / 2);
    const ids = [...this.channels.keys()];
    const gains = [Math.cos(t), Math.sin(t)];
    ids.forEach((id, index) => {
      this.channel(id).setCrossfadeGain(gains[index] ?? 1);
    });
  }

  getCrossfader(): number {
    return this.crossfaderPosition;
  }

  /**
   * Schedule a constant-power crossfade from the current position to `target`.
   *
   * Written as a sampled curve rather than a ramp because constant power is a
   * trigonometric shape, and a linear ramp between the same endpoints dips
   * audibly through the middle.
   */
  scheduleCrossfade(from: number, target: number, startAt: number, duration: number): void {
    // `from` is explicit. Taking it from the fader's current position was the
    // bug: the fader rests at centre, which leaves the incoming channel already
    // open at -3 dB, so a new track arrived at near full volume the instant it
    // started and the "fade" only took the old one away.
    const start = Math.max(-1, Math.min(1, from));
    const to = Math.max(-1, Math.min(1, target));
    const { first, second } = constantPowerCurves(start, to);

    const ids = [...this.channels.keys()];
    const curves = [first, second];
    ids.forEach((id, index) => {
      const curve = curves[index];
      if (curve) this.channel(id).scheduleCrossfadeCurve(curve, startAt, duration);
    });
    this.crossfaderPosition = to;
  }

  /**
   * Position implied by the gains actually in effect.
   *
   * A scheduled fade takes time to run, so the requested position and the real
   * one differ while it does. The UI shows this one.
   */
  observedCrossfader(): number {
    const strips = [...this.channels.values()];
    const a = strips[0]?.crossfadeGain() ?? 1;
    const b = strips[1]?.crossfadeGain() ?? 0;
    if (a === 0 && b === 0) return this.crossfaderPosition;
    return (Math.atan2(b, a) / (Math.PI / 2)) * 2 - 1;
  }

  /** Abandon any scheduled fade and hold where the crossfader is now (X2). */
  cancelScheduledCrossfade(): void {
    for (const strip of this.channels.values()) strip.cancelScheduled();
  }

  setMaster(position: number): void {
    this.masterPosition = Math.max(0, Math.min(1, position));
    ramp(this.master.gain, this.masterPosition * this.masterPosition);
  }

  getMaster(): number {
    return this.masterPosition;
  }

  read(): MeterReading {
    this.analyser.getFloatTimeDomainData(this.meterBuffer);
    let peak = 0;
    let sum = 0;
    for (const sample of this.meterBuffer) {
      const magnitude = Math.abs(sample);
      if (magnitude > peak) peak = magnitude;
      sum += sample * sample;
    }
    return {
      peak,
      rms: Math.sqrt(sum / this.meterBuffer.length),
      // Measured post-limiter, so this means the limiter is being driven hard
      // rather than that the output is actually clipped.
      clipping: peak > 0.99,
    };
  }
}
